import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  Ban,
  Check,
  Download,
  FileExclamationPoint,
  Hash,
  ListFilter,
  Plus,
  Radio,
  RefreshCcw,
  Settings2,
  Tablet,
  Wifi,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { DevicePosConfigModal } from "@/components/device/DevicePosConfigModal";
import { deviceKeys, deviceService } from "@/services/deviceService";
import { houseService } from "@/services/houseService";
import { hostService } from "@/services/hostService";
import { Permission, type Device } from "@/types";
import { CreateDeviceModal } from "./components/CreateDeviceModal";
import {
  Button,
  ConfirmModal,
  DefinitionList,
  DetailRow,
  Modal,
  ModalFooter,
  Tabs,
} from "@/components/ui";
import {
  PageFrame,
  ClickToCopyId,
  PickerPanel,
  AdminListToolbar,
  buildListResetToken,
  StatCard,
  StatCardGrid,
  selectableStatCardButtonClass,
} from "@/components/common";
import { useAuthStore } from "@/store/authStore";
import DevicePresetsPage from "./presets";
import {
  DevicesTable,
  type DeviceTableRow,
} from "@/components/device/DevicesTable";
import { DeviceCredentialsModal } from "@/components/device/DeviceCredentialsModal";
import { DeviceTelemetryPanel } from "@/components/device/DeviceTelemetryPanel";
import {
  compareAppVersionLabels,
  formatDeviceAppVersion,
  getSetupBadgeKind,
  getTelemetryStatus,
  matchesAppVersionFilters,
  matchesIncompleteMissingFilters,
  matchesSetupCompletenessFilter,
  matchesShelvedFilter,
  matchesTelemetryStatusFilter,
  telemetryStatusDotClass,
  type DeviceAppVersionLabels,
  type SetupCompletenessFilter,
  type SetupMissingKind,
  type ShelvedFilter,
  type TelemetryStatusFilter,
} from "@/lib/deviceStatus";
import { downloadCsv } from "@/lib/csv";
import { formatDateTime } from "@/lib/datetime";

import { AdminFilterMenu } from "@/components/admin/AdminFilterMenu";
import {
  AdminRegionFilter,
  type AdminRegionFilterValue,
} from "@/components/admin/AdminRegionFilter";
import { AdminStatusFilter } from "@/components/admin/AdminStatusFilter";
import {
  countByStatusFilter,
  matchesStatusFilter,
  type StatusFilter,
} from "@/components/admin/adminListStatus";
import { getApiErrorMessage, toastApiError } from "@/lib/errorMessages";
import { isReaderPaired } from "@/lib/readerLabel";
import { matchesTextQuery } from "@/lib/textSearch";
import { useNavigate, useSearchParams } from "react-router";

const DEVICE_CSV_HEADERS = [
  "display_name",
  "kiosk_code",
  "serial_number",
  "google_account",
  "freekiosk_unlock_code",
  "reader_serial",
  "region_code",
  "house",
  "app_version",
  "brand",
  "model",
  "android_version",
  "is_revoked",
  "is_shelved",
  "notes",
  "software_id",
  "last_telemetry_at",
  "last_interaction_at",
] as const;

type DevicesPageTab = "devices" | "presets";

function parseDevicesPageTab(value: string | null): DevicesPageTab {
  return value === "presets" ? "presets" : "devices";
}

const SETUP_FILTER_OPTIONS: SetupCompletenessFilter[] = [
  "all",
  "complete",
  "incomplete",
  "revoked",
];

const SETUP_FILTER_LABEL_KEYS: Record<SetupCompletenessFilter, string> = {
  all: "page.filterSetupAll",
  complete: "page.filterSetupComplete",
  incomplete: "page.filterSetupIncomplete",
  revoked: "page.filterSetupRevoked",
};

const SHELVED_FILTER_OPTIONS: ShelvedFilter[] = [
  "all",
  "in_service",
  "shelved",
];

const SHELVED_FILTER_LABEL_KEYS: Record<ShelvedFilter, string> = {
  all: "page.filterShelvedAll",
  in_service: "page.filterShelvedInService",
  shelved: "page.filterShelvedOnly",
};

const SETUP_FILTER_OPTION_VISUAL: Partial<
  Record<
    SetupCompletenessFilter,
    { icon: typeof Check; iconBadgeClassName: string }
  >
> = {
  complete: {
    icon: Check,
    iconBadgeClassName: "text-success-600",
  },
  incomplete: {
    icon: FileExclamationPoint,
    iconBadgeClassName: "text-warning-600",
  },
  revoked: {
    icon: Ban,
    iconBadgeClassName: "text-error-600",
  },
};

const MISSING_KIND_OPTIONS: SetupMissingKind[] = [
  "binding",
  "pairing",
  "assignment",
];

const MISSING_KIND_LABEL_KEYS: Record<SetupMissingKind, string> = {
  binding: "page.filterMissingBinding",
  pairing: "page.filterMissingReader",
  assignment: "page.filterMissingStructure",
};

const TELEMETRY_FILTER_OPTIONS: TelemetryStatusFilter[] = [
  "all",
  "gray",
  "green",
  "yellow",
  "red",
];

const TELEMETRY_FILTER_LABEL_KEYS: Record<TelemetryStatusFilter, string> = {
  all: "page.filterTelemetryAll",
  gray: "page.filterTelemetryGray",
  green: "page.filterTelemetryGreen",
  yellow: "page.filterTelemetryYellow",
  red: "page.filterTelemetryRed",
};

export default function DevicesPage() {
  const { t } = useTranslation("devices");
  const { t: tCommon } = useTranslation("common");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusDeviceId = searchParams.get("deviceId");
  const didApplyDeviceFocusRef = useRef(false);
  const queryClient = useQueryClient();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canManageDevices = hasPermission(Permission.DEVICES_READ);
  const activeTab = parseDevicesPageTab(searchParams.get("tab"));
  const setActiveTab = useCallback(
    (tab: DevicesPageTab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab === "devices") {
            next.delete("tab");
          } else {
            next.set("tab", tab);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [setupFilter, setSetupFilter] =
    useState<SetupCompletenessFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [shelvedFilter, setShelvedFilter] = useState<ShelvedFilter>("all");
  const [telemetryFilter, setTelemetryFilter] =
    useState<TelemetryStatusFilter>("all");
  const [incompleteMissingFilters, setIncompleteMissingFilters] = useState<
    SetupMissingKind[]
  >([]);
  const [appVersionFilters, setAppVersionFilters] = useState<string[]>([]);
  const [regionFilter, setRegionFilter] =
    useState<AdminRegionFilterValue>(null);
  const [otherInfoDevice, setOtherInfoDevice] = useState<DeviceTableRow | null>(
    null,
  );

  // Assignment wizard state
  const [assigningDevice, setAssigningDevice] = useState<DeviceTableRow | null>(
    null,
  );
  const [hostSearchQuery, setHostSearchQuery] = useState("");
  const [houseSearchQuery, setHouseSearchQuery] = useState("");
  const [selectedHostId, setSelectedHostId] = useState("");
  const [selectedHouseId, setSelectedHouseId] = useState("");
  const [isAssigning, setIsAssigning] = useState(false);
  const [posConfigKiosk, setPosConfigKiosk] = useState<DeviceTableRow | null>(
    null,
  );
  const [credentialModalState, setCredentialModalState] = useState<{
    kioskCode: string;
    password: string;
    title: string;
    description: string;
  } | null>(null);
  const [pendingAction, setPendingAction] = useState<{
    kind: "regenerate" | "unpair" | "unassign";
    device: DeviceTableRow;
  } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<Set<string>>(
    () => new Set(),
  );

  const deviceListParams = { region_id: regionFilter ?? undefined };
  const {
    data: kiosks = [],
    isLoading,
    isFetching,
    error: devicesQueryError,
    refetch: refetchDevices,
  } = useQuery({
    queryKey: deviceKeys.list(deviceListParams),
    queryFn: () => deviceService.getAll(deviceListParams),
    enabled: canManageDevices,
  });
  const error = devicesQueryError
    ? getApiErrorMessage(devicesQueryError, "loadDevices")
    : null;

  const { data: hosts = [], isFetching: isLoadingHosts, isError: hostsError, error: hostsQueryError } = useQuery({
    queryKey: ["hosts", "all"],
    queryFn: () => hostService.getAll(),
    enabled: assigningDevice != null,
  });

  useEffect(() => {
    if (hostsError) {
      toastApiError(hostsQueryError);
    }
  }, [hostsError, hostsQueryError]);

  const {
    data: selectedHostHousesResponse,
    isFetching: isLoadingHouses,
    isError: housesByHostError,
    error: housesByHostQueryError,
  } = useQuery({
    queryKey: ["houses", "by-host", selectedHostId],
    queryFn: () => houseService.listByHost(selectedHostId),
    enabled: Boolean(selectedHostId),
  });
  const selectedHostHouses = useMemo(
    () => selectedHostHousesResponse ?? [],
    [selectedHostHousesResponse],
  );

  useEffect(() => {
    if (housesByHostError) {
      toastApiError(housesByHostQueryError);
    }
  }, [housesByHostError, housesByHostQueryError]);

  const invalidateDeviceLists = async () => {
    await queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
  };

  const patchDeviceRevokedInCaches = (deviceId: string, isRevoked: boolean) => {
    queryClient.setQueriesData<Device[]>(
      { queryKey: deviceKeys.lists() },
      (current) =>
        current?.map((item) =>
          item.id === deviceId ? { ...item, is_revoked: isRevoked } : item,
        ),
    );
    queryClient.setQueriesData<Device[]>(
      { queryKey: ["house-kiosks"] },
      (current) =>
        current?.map((item) =>
          item.id === deviceId ? { ...item, is_revoked: isRevoked } : item,
        ),
    );
  };

  const patchDeviceShelvedInCaches = (deviceId: string, isShelved: boolean) => {
    queryClient.setQueriesData<Device[]>(
      { queryKey: deviceKeys.lists() },
      (current) =>
        current?.map((item) =>
          item.id === deviceId ? { ...item, is_shelved: isShelved } : item,
        ),
    );
    queryClient.setQueriesData<Device[]>(
      { queryKey: ["house-kiosks"] },
      (current) =>
        current?.map((item) =>
          item.id === deviceId ? { ...item, is_shelved: isShelved } : item,
        ),
    );
  };

  const assignedDeviceCountByHouse = useMemo(() => {
    const counts = new Map<string, number>();
    for (const kiosk of kiosks) {
      if (!kiosk.house_id) continue;
      counts.set(kiosk.house_id, (counts.get(kiosk.house_id) ?? 0) + 1);
    }
    return counts;
  }, [kiosks]);

  const filteredHosts = useMemo(() => {
    if (!hostSearchQuery.trim()) return hosts;
    return hosts.filter((host) => matchesTextQuery(hostSearchQuery, [host.name]));
  }, [hostSearchQuery, hosts]);

  const appVersionLabels = useMemo<DeviceAppVersionLabels>(
    () => ({
      unknown: t("page.valueAppVersionUnknown"),
      legacy: t("page.valueAppVersionLegacy"),
    }),
    [t],
  );

  const availableAppVersions = useMemo(() => {
    const versions = new Set<string>();
    for (const kiosk of kiosks) {
      versions.add(formatDeviceAppVersion(kiosk, appVersionLabels));
    }
    return [...versions].sort((a, b) =>
      compareAppVersionLabels(a, b, appVersionLabels),
    );
  }, [appVersionLabels, kiosks]);

  const filteredHouses = useMemo(() => {
    if (!houseSearchQuery.trim()) return selectedHostHouses;
    return selectedHostHouses.filter((house) =>
      matchesTextQuery(houseSearchQuery, [house.name, house.address]),
    );
  }, [houseSearchQuery, selectedHostHouses]);

  const statusFilterCounts = useMemo(
    () => countByStatusFilter(kiosks, (kiosk) => !kiosk.is_revoked),
    [kiosks],
  );

  const deviceKpis = useMemo(() => {
    const active = kiosks.filter((kiosk) => !kiosk.is_revoked);
    return {
      total: active.length,
      online: active.filter((kiosk) => getTelemetryStatus(kiosk) === "green")
        .length,
      incomplete: active.filter(
        (kiosk) => getSetupBadgeKind(kiosk) === "incomplete",
      ).length,
      shelved: active.filter((kiosk) => kiosk.is_shelved === true).length,
    };
  }, [kiosks]);

  const filteredKiosks = useMemo(() => {
    return kiosks.filter((kiosk) => {
      if (!matchesStatusFilter(!kiosk.is_revoked, statusFilter)) {
        return false;
      }
      if (!matchesShelvedFilter(kiosk, shelvedFilter)) {
        return false;
      }
      if (!matchesSetupCompletenessFilter(kiosk, setupFilter)) {
        return false;
      }
      if (!matchesTelemetryStatusFilter(kiosk, telemetryFilter)) {
        return false;
      }
      if (
        setupFilter === "incomplete" &&
        !matchesIncompleteMissingFilters(kiosk, incompleteMissingFilters)
      ) {
        return false;
      }
      if (
        !matchesAppVersionFilters(kiosk, appVersionFilters, appVersionLabels)
      ) {
        return false;
      }
      if (!matchesTextQuery(searchQuery, [
        kiosk.display_name,
        kiosk.kiosk_code,
        kiosk.serial_number,
        kiosk.google_account,
        kiosk.model,
        kiosk.house?.name,
        kiosk.brand,
        kiosk.notes,
      ], { trim: false })) {
        return false;
      }
      return true;
    });
  }, [
    appVersionFilters,
    appVersionLabels,
    incompleteMissingFilters,
    kiosks,
    searchQuery,
    setupFilter,
    shelvedFilter,
    statusFilter,
    telemetryFilter,
  ]);

  useEffect(() => {
    didApplyDeviceFocusRef.current = false;
  }, [focusDeviceId]);

  useEffect(() => {
    if (!focusDeviceId || didApplyDeviceFocusRef.current || isLoading) {
      return;
    }
    const device = kiosks.find((kiosk) => kiosk.id === focusDeviceId);
    if (!device) {
      if (regionFilter !== null) {
        setRegionFilter(null);
      }
      return;
    }
    didApplyDeviceFocusRef.current = true;
    setActiveTab("devices");
    setSetupFilter("all");
    setStatusFilter("all");
    setShelvedFilter("all");
    setTelemetryFilter("all");
    setIncompleteMissingFilters([]);
    setAppVersionFilters([]);
    setSearchQuery(device.serial_number);
  }, [focusDeviceId, isLoading, kiosks, regionFilter, setActiveTab]);

  useEffect(() => {
    if (!focusDeviceId || !didApplyDeviceFocusRef.current) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      document
        .getElementById(`device-row-${focusDeviceId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusDeviceId, filteredKiosks]);

  const kiosksById = useMemo(() => {
    const byId = new Map<string, DeviceTableRow>();
    for (const kiosk of kiosks) {
      byId.set(kiosk.id, kiosk);
    }
    return byId;
  }, [kiosks]);

  useEffect(() => {
    setSelectedDeviceIds((current) => {
      let changed = false;
      const next = new Set<string>();
      for (const id of current) {
        if (kiosksById.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [kiosksById]);

  const handleSelectAllFiltered = () => {
    setSelectedDeviceIds(new Set(filteredKiosks.map((device) => device.id)));
  };

  const allFilteredSelected =
    filteredKiosks.length > 0 &&
    filteredKiosks.every((device) => selectedDeviceIds.has(device.id));

  const handleExportSelectedCsv = () => {
    const selectedDevices = [...selectedDeviceIds]
      .map((id) => kiosksById.get(id))
      .filter((device): device is DeviceTableRow => device != null);

    if (selectedDevices.length === 0) {
      return;
    }

    const rows = selectedDevices.map((device) => [
      device.display_name,
      device.kiosk_code,
      device.serial_number,
      device.google_account,
      device.freekiosk_unlock_code ?? "",
      device.reader?.serial_number ?? "",
      device.region_code ?? "",
      device.house?.name ?? "",
      formatDeviceAppVersion(device, appVersionLabels),
      device.brand ?? "",
      device.model ?? "",
      device.android_version ?? "",
      device.is_revoked === true ? "true" : "false",
      device.is_shelved === true ? "true" : "false",
      device.notes ?? "",
      device.software_id ?? "",
      device.last_device_report_at
        ? formatDateTime(device.last_device_report_at)
        : "",
      device.last_interaction_at
        ? formatDateTime(device.last_interaction_at)
        : "",
    ]);

    const dateStamp = new Date().toISOString().slice(0, 10);
    downloadCsv(
      `devices-export-${dateStamp}.csv`,
      [...DEVICE_CSV_HEADERS],
      rows,
    );
  };

  const handleUpdateDisplayName = async (
    kiosk: DeviceTableRow,
    displayName: string,
  ) => {
    try {
      await deviceService.update(kiosk.id, {
        display_name: displayName,
      });
      await invalidateDeviceLists();
      toast.success(t("deviceRenamed"));
    } catch (err) {
      toastApiError(err, "renameDevice");
      throw err;
    }
  };

  const handleUpdateGoogleAccount = async (
    kiosk: DeviceTableRow,
    googleAccount: string,
  ) => {
    try {
      await deviceService.update(kiosk.id, {
        google_account: googleAccount,
      });
      await invalidateDeviceLists();
      toast.success(t("deviceGoogleAccountUpdated"));
    } catch (err) {
      toastApiError(err, "updateDevice");
      throw err;
    }
  };

  const handleUpdateNotes = async (
    kiosk: DeviceTableRow,
    notes: string | null,
  ) => {
    try {
      await deviceService.update(kiosk.id, { notes });
      await invalidateDeviceLists();
      toast.success(t("deviceNotesUpdated"));
    } catch (err) {
      toastApiError(err, "updateDevice");
      throw err;
    }
  };

  const handleToggleActive = async (kiosk: Device) => {
    const previousRevoked = kiosk.is_revoked === true;
    const nextRevoked = !previousRevoked;
    patchDeviceRevokedInCaches(kiosk.id, nextRevoked);
    try {
      await deviceService.update(kiosk.id, { is_revoked: nextRevoked });
      toast.success(
        previousRevoked ? t("deviceReactivated") : t("deviceRevoked"),
      );
      void invalidateDeviceLists();
    } catch (err) {
      patchDeviceRevokedInCaches(kiosk.id, previousRevoked);
      toastApiError(err, "updateDevice");
    }
  };

  const handleToggleShelved = async (kiosk: Device) => {
    const previousShelved = kiosk.is_shelved === true;
    const nextShelved = !previousShelved;
    patchDeviceShelvedInCaches(kiosk.id, nextShelved);
    try {
      await deviceService.update(kiosk.id, { is_shelved: nextShelved });
      toast.success(
        previousShelved ? t("deviceUnshelved") : t("deviceShelved"),
      );
      void invalidateDeviceLists();
    } catch (err) {
      patchDeviceShelvedInCaches(kiosk.id, previousShelved);
      toastApiError(err, "updateDevice");
    }
  };

  const closeAssignModal = () => {
    setAssigningDevice(null);
    setHostSearchQuery("");
    setHouseSearchQuery("");
    setSelectedHostId("");
    setSelectedHouseId("");
  };

  const handleRegenerateCredentials = (kiosk: DeviceTableRow) => {
    setPendingAction({ kind: "regenerate", device: kiosk });
  };

  const handleAssignDevice = async () => {
    if (!assigningDevice || !selectedHouseId) {
      return;
    }
    try {
      setIsAssigning(true);
      await deviceService.assignToHouse(assigningDevice.id, selectedHouseId);
      await invalidateDeviceLists();
      await queryClient.invalidateQueries({
        queryKey: deviceKeys.byHouse(selectedHouseId),
      });
      await queryClient.invalidateQueries({
        queryKey: ["house", selectedHouseId],
      });
      toast.success(t("kioskAssigned"));
      closeAssignModal();
    } catch (err) {
      toastApiError(err, "assignDevice");
    } finally {
      setIsAssigning(false);
    }
  };

  const handleOpenAssignDevice = (device: DeviceTableRow) => {
    setAssigningDevice(device);
    setHostSearchQuery("");
    setHouseSearchQuery("");
    setSelectedHostId("");
    setSelectedHouseId("");
  };

  const handleOpenPairing = (device: DeviceTableRow) => {
    if (!device.house_id) {
      toast.error(t("assignBeforePairing"));
      return;
    }
    setPosConfigKiosk(device);
  };

  const handleUnpairReader = (device: DeviceTableRow) => {
    if (!isReaderPaired(device.reader)) return;
    setPendingAction({ kind: "unpair", device });
  };

  const handleRemoveHouse = (device: DeviceTableRow) => {
    if (!device.house_id) return;
    setPendingAction({ kind: "unassign", device });
  };

  const handleConfirmPendingAction = async () => {
    if (!pendingAction) return;
    const { kind, device } = pendingAction;
    setActionBusy(true);
    try {
      if (kind === "regenerate") {
        const response = await deviceService.resetPassword(device.id);
        setCredentialModalState({
          kioskCode: device.kiosk_code,
          password: response.password,
          title: t("page.credentialModalTitle"),
          description: t("page.credentialModalDesc"),
        });
        await invalidateDeviceLists();
        toast.success(t("credentialsRegenerated"));
      } else if (kind === "unpair") {
        await deviceService.unlinkReader(device.id);
        await invalidateDeviceLists();
        if (device.house_id) {
          await queryClient.invalidateQueries({
            queryKey: deviceKeys.byHouse(device.house_id),
          });
        }
        toast.success(t("page.readerUnpaired"));
      } else {
        const houseId = device.house_id;
        await deviceService.unassignFromHouse(device.id);
        await invalidateDeviceLists();
        if (houseId) {
          await queryClient.invalidateQueries({
            queryKey: deviceKeys.byHouse(houseId),
          });
          await queryClient.invalidateQueries({
            queryKey: ["house", houseId],
          });
        }
        toast.success(t("page.houseRemoved"));
      }
      setPendingAction(null);
    } catch (err) {
      toastApiError(
        err,
        kind === "regenerate"
          ? "regenerateCredentials"
          : kind === "unpair"
            ? "unlinkReader"
            : "removeHouse",
      );
    } finally {
      setActionBusy(false);
    }
  };

  const tableLabels = {
    colDevice: t("page.colDevice"),
    colSerial: t("page.colSerial"),
    colGoogleAccount: t("page.colGoogleAccount"),
    colFreekioskUnlock: t("page.colFreekioskUnlock"),
    colReader: t("page.colReader"),
    colHouse: t("page.colHouse"),
    colLastTelemetry: t("page.colLastTelemetry"),
    colLastInteraction: t("page.colLastInteraction"),
    colAppVersion: t("shared.appVersion"),
    colInUse: t("page.colInUse"),
    colActions: t("page.colActions"),
    emptyDefault: t("page.emptyDefault"),
    readerPair: t("page.readerPair"),
    readerUnpairTitle: t("page.readerUnpairTitle"),
    assign: t("page.assign"),
    houseRemoveTitle: t("page.houseRemoveTitle"),
    houseOpenTitle: t("page.houseOpenTitle"),
    lastTelemetryNever: t("page.valueTelemetryNever"),
    valueAppVersionUnknown: t("page.valueAppVersionUnknown"),
    valueAppVersionLegacy: t("page.valueAppVersionLegacy"),
    valueFreekioskUnlockUnavailable: t("page.valueFreekioskUnlockUnavailable"),
    moreActionsTitle: t("page.moreActionsTitle"),
    menuRegenerateCredentials: t("page.menuRegenerateCredentials"),
    menuRevokeDevice: t("page.menuRevokeDevice"),
    menuReactivateDevice: t("page.menuReactivateDevice"),
    menuNotes: t("page.menuNotes"),
    menuOtherInfo: t("page.menuOtherInfo"),
    renameDeviceTitle: t("page.renameDeviceTitle"),
    displayNamePlaceholder: t("page.displayNamePlaceholder"),
    displayNameEmpty: t("page.displayNameEmpty"),
    editGoogleAccountTitle: t("page.editGoogleAccountTitle"),
    editNotesTitle: t("page.editNotesTitle"),
    notesPlaceholder: t("page.notesPlaceholder"),
    telemetryBattery: t("page.telemetryBattery"),
    telemetryWifi: t("page.telemetryWifi"),
    telemetryAudio: t("page.telemetryAudio"),
    telemetryStorage: t("page.telemetryStorage"),
    telemetryMemory: t("page.telemetryMemory"),
    telemetryUnavailable: t("page.telemetryUnavailable"),
    telemetryDataAge: (age: string) => t("page.telemetryDataAge", { age }),
  };

  const filterResetToken = buildListResetToken([
    searchQuery,
    statusFilter,
    shelvedFilter,
    setupFilter,
    telemetryFilter,
    incompleteMissingFilters.join(","),
    appVersionFilters.join(","),
    regionFilter ?? "",
  ]);

  return (
    <PageFrame
      title={t("page.title")}
      description={t("page.subtitle")}
      icon={activeTab === "presets" ? Settings2 : Tablet}
      actions={
        activeTab === "devices" ? (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              aria-label={tCommon("refresh")}
              isLoading={isFetching}
              leftIcon={<RefreshCcw className="h-4 w-4" />}
              onClick={() => void refetchDevices()}
            />
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              {t("page.newDevice")}
            </Button>
          </div>
        ) : undefined
      }
    >
      <Tabs
        variant="segmented"
        ariaLabel={t("page.title")}
        idPrefix="devices"
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabs={[
          {
            id: "devices",
            label: t("page.tabDevices"),
            icon: <Tablet className="h-4 w-4" />,
          },
          {
            id: "presets",
            label: t("page.tabPresets"),
            icon: <Settings2 className="h-4 w-4" />,
          },
        ]}
      />

      {activeTab === "presets" ? <DevicePresetsPage /> : null}

      {activeTab === "devices" ? (
        <>
          <StatCardGrid
            layout="1-2-4"
            className="animate-slide-up grid-cols-2 gap-3 sm:gap-4 lg:gap-4"
          >
            <StatCard
              title={t("page.kpiTotal")}
              value={deviceKpis.total}
              icon={Tablet}
              iconColor="primary"
              subtitle={t("page.kpiTotalHint")}
            />
            <button
              type="button"
              className={selectableStatCardButtonClass}
              aria-pressed={telemetryFilter === "green"}
              onClick={() =>
                setTelemetryFilter(telemetryFilter === "green" ? "all" : "green")
              }
            >
              <StatCard
                title={t("page.kpiOnline")}
                value={deviceKpis.online}
                icon={Wifi}
                iconColor="success"
                subtitle={t("page.kpiOnlineHint")}
                selected={telemetryFilter === "green"}
              />
            </button>
            <button
              type="button"
              className={selectableStatCardButtonClass}
              aria-pressed={setupFilter === "incomplete"}
              onClick={() =>
                setSetupFilter(setupFilter === "incomplete" ? "all" : "incomplete")
              }
            >
              <StatCard
                title={t("page.kpiIncomplete")}
                value={deviceKpis.incomplete}
                icon={FileExclamationPoint}
                iconColor="warning"
                subtitle={t("page.kpiIncompleteHint")}
                selected={setupFilter === "incomplete"}
              />
            </button>
            <button
              type="button"
              className={selectableStatCardButtonClass}
              aria-pressed={shelvedFilter === "shelved"}
              onClick={() =>
                setShelvedFilter(shelvedFilter === "shelved" ? "all" : "shelved")
              }
            >
              <StatCard
                title={t("page.kpiShelved")}
                value={deviceKpis.shelved}
                icon={Archive}
                iconColor="accent"
                subtitle={t("page.kpiShelvedHint")}
                selected={shelvedFilter === "shelved"}
              />
            </button>
          </StatCardGrid>

          <AdminListToolbar
            search={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder={t("page.searchPlaceholder")}
            filterChildren={
              <>
                <AdminStatusFilter
                  value={statusFilter}
                  onChange={setStatusFilter}
                  counts={statusFilterCounts}
                  labels={{
                    name: t("page.filterActiveLabel"),
                    all: t("page.filterActiveAll"),
                    active: t("page.filterActiveActive"),
                    inactive: t("page.filterActiveInactive"),
                  }}
                />
                <AdminFilterMenu
                  icon={Archive}
                  name={t("page.filterShelvedLabel")}
                  value={shelvedFilter}
                  defaultValue="all"
                  onChange={setShelvedFilter}
                  options={SHELVED_FILTER_OPTIONS.map((option) => ({
                    value: option,
                    label: t(SHELVED_FILTER_LABEL_KEYS[option]),
                  }))}
                />
                <AdminFilterMenu
                  icon={Settings2}
                  name={t("page.filterSetupLabel")}
                  value={setupFilter}
                  defaultValue="all"
                  onChange={setSetupFilter}
                  options={SETUP_FILTER_OPTIONS.map((option) => ({
                    value: option,
                    label: t(SETUP_FILTER_LABEL_KEYS[option]),
                    ...SETUP_FILTER_OPTION_VISUAL[option],
                  }))}
                />
                {setupFilter === "incomplete" ? (
                  <AdminFilterMenu
                    mode="multiple"
                    icon={ListFilter}
                    name={t("page.filterIncompleteMissingLabel")}
                    value={incompleteMissingFilters}
                    onChange={setIncompleteMissingFilters}
                    anyLabel={t("page.filterIncompleteMissingAny")}
                    countLabel={(count) =>
                      t("page.filterIncompleteMissingCount", { count })
                    }
                    options={MISSING_KIND_OPTIONS.map((kind) => ({
                      value: kind,
                      label: t(MISSING_KIND_LABEL_KEYS[kind]),
                    }))}
                  />
                ) : null}
                <AdminFilterMenu
                  icon={Radio}
                  name={t("page.filterTelemetryLabel")}
                  value={telemetryFilter}
                  defaultValue="all"
                  onChange={setTelemetryFilter}
                  options={TELEMETRY_FILTER_OPTIONS.map((option) => ({
                    value: option,
                    label: t(TELEMETRY_FILTER_LABEL_KEYS[option]),
                    dotClassName:
                      option === "all"
                        ? undefined
                        : telemetryStatusDotClass(option),
                  }))}
                />
                <AdminFilterMenu
                  mode="multiple"
                  icon={Hash}
                  name={t("shared.appVersion")}
                  value={appVersionFilters}
                  onChange={setAppVersionFilters}
                  anyLabel={t("page.filterAppVersionAny")}
                  countLabel={(count) =>
                    t("page.filterAppVersionCount", { count })
                  }
                  options={availableAppVersions.map((version) => ({
                    value: version,
                    label: version,
                  }))}
                />
                <AdminRegionFilter
                  value={regionFilter}
                  onChange={setRegionFilter}
                  valueKey="id"
                />
              </>
            }
          />

          <DevicesTable
            devices={filteredKiosks}
            labels={tableLabels}
            preferencesKey="devices"
            showHouseColumn
            loadState={
              isLoading
                ? { status: "loading" }
                : error != null
                  ? {
                      status: "error",
                      message: error,
                      retryLabel: tCommon("tryAgain"),
                      onRetry: () => void refetchDevices(),
                    }
                  : undefined
            }
            resetToken={filterResetToken}
            selectedIds={selectedDeviceIds}
            onSelectedIdsChange={setSelectedDeviceIds}
            selectionBulkAction={{
              label: t("page.selectAllFiltered", {
                count: filteredKiosks.length,
              }),
              onClick: handleSelectAllFiltered,
              hidden: allFilteredSelected || filteredKiosks.length === 0,
            }}
            toolbar={
              selectedDeviceIds.size > 0 ? (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={handleExportSelectedCsv}
                >
                  <Download className="h-4 w-4" />
                  {t("page.exportCsv")}
                </Button>
              ) : null
            }
            emptyMessage={
              searchQuery ? t("page.emptySearch") : t("page.emptyDefault")
            }
            onPairReader={handleOpenPairing}
            onUnpairReader={(device) => void handleUnpairReader(device)}
            onAssignHouse={handleOpenAssignDevice}
            onRemoveHouse={(device) => void handleRemoveHouse(device)}
            onOpenHouse={(houseId) => navigate(`/houses/${houseId}`)}
            onRegenerateCredentials={(device) =>
              void handleRegenerateCredentials(device)
            }
            onToggleActive={(device) => void handleToggleActive(device)}
            onToggleShelved={(device) => void handleToggleShelved(device)}
            onShowOtherInfo={setOtherInfoDevice}
            onUpdateDisplayName={handleUpdateDisplayName}
            onUpdateGoogleAccount={handleUpdateGoogleAccount}
            onUpdateNotes={handleUpdateNotes}
          />
        </>
      ) : null}

      <DevicePosConfigModal
        isOpen={!!posConfigKiosk}
        device={posConfigKiosk}
        onClose={() => setPosConfigKiosk(null)}
        onUpdated={async () => {
          await invalidateDeviceLists();
          if (posConfigKiosk?.house_id) {
            await queryClient.invalidateQueries({
              queryKey: ["house", posConfigKiosk.house_id],
            });
            await queryClient.invalidateQueries({
              queryKey: deviceKeys.byHouse(posConfigKiosk.house_id),
            });
          }
          toast.success(t("readerUpdated"));
        }}
      />

      {/* Create Device Modal */}
      <CreateDeviceModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          void invalidateDeviceLists();
          setIsCreateModalOpen(false);
        }}
      />

      <DeviceCredentialsModal
        isOpen={credentialModalState !== null}
        title={credentialModalState?.title ?? ""}
        description={credentialModalState?.description ?? ""}
        kioskCode={credentialModalState?.kioskCode ?? ""}
        password={credentialModalState?.password ?? ""}
        onClose={() => setCredentialModalState(null)}
      />

      <Modal
        closeLabel={tCommon("close")}
        isOpen={assigningDevice != null}
        onClose={closeAssignModal}
        title={t("page.assignModalTitle")}
        size="xl"
        footer={
          <ModalFooter>
            <Button variant="outline" onClick={closeAssignModal}>
              {tCommon("cancel")}
            </Button>
            <Button
              onClick={() => void handleAssignDevice()}
              disabled={!selectedHouseId || isAssigning}
              isLoading={isAssigning}
            >
              {t("page.assignSubmit")}
            </Button>
          </ModalFooter>
        }
      >
        {assigningDevice ? (
          <>
            <p className="mb-4 text-[13px] text-surface-500">
              {t("page.assignModalIntro", {
                name: assigningDevice.display_name,
              })}
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <PickerPanel
                stepLabel={t("page.selectHostStep")}
                searchPlaceholder={t("page.searchHostsPlaceholder")}
                searchValue={hostSearchQuery}
                onSearchChange={setHostSearchQuery}
                isLoading={false}
                isRefreshing={isLoadingHosts}
                emptyMessage={t("page.noHostsFound")}
                items={filteredHosts.map((host) => ({
                  id: host.user_id,
                  title: host.name,
                  selected: selectedHostId === host.user_id,
                  onSelect: () => {
                    setSelectedHostId(host.user_id);
                    setSelectedHouseId("");
                    setHouseSearchQuery("");
                  },
                }))}
              />
              <PickerPanel
                stepLabel={t("page.selectHouseStep")}
                searchPlaceholder={
                  selectedHostId
                    ? t("page.searchHousesPlaceholder")
                    : t("page.searchHousesDisabledPlaceholder")
                }
                searchValue={houseSearchQuery}
                onSearchChange={setHouseSearchQuery}
                searchDisabled={!selectedHostId}
                isLoading={Boolean(selectedHostId && isLoadingHouses)}
                emptyMessage={
                  selectedHostId
                    ? t("page.noHousesFound")
                    : t("page.pickHostForHouses")
                }
                items={filteredHouses.map((house) => ({
                  id: house.id,
                  title: house.name,
                  subtitle: house.address || t("page.noAddressSet"),
                  detail: `${t("page.assignedDevicesLabel")} ${assignedDeviceCountByHouse.get(house.id) ?? 0}`,
                  selected: selectedHouseId === house.id,
                  onSelect: () => setSelectedHouseId(house.id),
                }))}
              />
            </div>
          </>
        ) : null}
      </Modal>

      <Modal
        closeLabel={tCommon("close")}
        isOpen={otherInfoDevice != null}
        onClose={() => setOtherInfoDevice(null)}
        title={t("page.otherInfoTitle")}
        size="lg"
        footer={
          <ModalFooter>
            <Button onClick={() => setOtherInfoDevice(null)}>
              {tCommon("close")}
            </Button>
          </ModalFooter>
        }
      >
        {otherInfoDevice ? (
          <>
            <DefinitionList className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              <DetailRow label={t("page.fieldDisplayName")} valueClassName="font-medium">
                {otherInfoDevice.display_name}
              </DetailRow>
              <DetailRow label={t("page.fieldKioskCode")} valueClassName="font-mono">
                {otherInfoDevice.kiosk_code}
              </DetailRow>
              <DetailRow label={t("page.fieldSerialNumber")}>
                <ClickToCopyId
                  value={otherInfoDevice.serial_number}
                  ellipsis="start"
                />
              </DetailRow>
              <DetailRow label={t("page.fieldGoogleAccount")} valueClassName="break-all">
                {otherInfoDevice.google_account}
              </DetailRow>
              <DetailRow label={t("page.fieldSoftwareId")} valueClassName="break-all font-mono text-[13px]">
                {otherInfoDevice.software_id || t("page.valueUnbound")}
              </DetailRow>
              <DetailRow label={t("page.fieldReaderId")} valueClassName="break-all font-mono text-[13px]">
                {otherInfoDevice.reader?.serial_number ||
                  t("page.valueNotPaired")}
              </DetailRow>
              <DetailRow label={t("shared.brand")}>
                {otherInfoDevice.brand || "-"}
              </DetailRow>
              <DetailRow label={t("page.fieldDisplayInches")} valueClassName="tabular-nums">
                {otherInfoDevice.display_inches != null
                  ? `${otherInfoDevice.display_inches}"`
                  : "-"}
              </DetailRow>
              <DetailRow label={t("page.fieldResolution")} valueClassName="tabular-nums">
                {otherInfoDevice.display_resolution || "-"}
              </DetailRow>
              <DetailRow label={t("page.fieldAndroidVersion")} valueClassName="tabular-nums">
                {otherInfoDevice.android_version || "-"}
              </DetailRow>
              <DetailRow label={t("shared.appVersion")} valueClassName="font-mono text-[13px]">
                {formatDeviceAppVersion(otherInfoDevice, {
                  unknown: t("page.valueAppVersionUnknown"),
                  legacy: t("page.valueAppVersionLegacy"),
                })}
              </DetailRow>
              <DetailRow label={t("page.fieldHouse")}>
                {otherInfoDevice.house?.name || t("page.valueUnassigned")}
              </DetailRow>
            </DefinitionList>
              <DeviceTelemetryPanel
                device={otherInfoDevice}
                labels={{
                  title: t("page.sectionTelemetryData"),
                  lastReport: t("page.fieldLastTelemetryReport"),
                  sectionDeviceStatus: t("page.sectionTelemetryDeviceStatus"),
                  sectionGpsLocation: t("page.sectionTelemetryGpsLocation"),
                  neverReceived: t("page.valueTelemetryNever"),
                  emptyPayload: t("page.valueTelemetryEmpty"),
                }}
              />
          </>
        ) : null}
      </Modal>

      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
        confirmText={tCommon("confirm")}
        isOpen={pendingAction != null}
        onClose={() => {
          if (!actionBusy) setPendingAction(null);
        }}
        onConfirm={() => void handleConfirmPendingAction()}
        title={
          pendingAction?.kind === "regenerate"
            ? t("page.menuRegenerateCredentials")
            : pendingAction?.kind === "unpair"
              ? t("page.readerUnpairTitle")
              : t("page.houseRemoveTitle")
        }
        message={
          pendingAction?.kind === "regenerate"
            ? t("page.regenerateConfirm", {
                name: pendingAction.device.display_name,
              })
            : pendingAction?.kind === "unpair"
              ? t("page.readerUnpairConfirm")
              : t("page.houseRemoveConfirm", {
                  name: pendingAction?.device.house?.name ?? "",
                })
        }
        isDestructive={pendingAction?.kind !== "regenerate"}
        isLoading={actionBusy}
      />
    </PageFrame>
  );
}
